<script setup lang="ts">
  import { computed, onMounted, onUnmounted, provide, reactive, ref, watch } from 'vue';
  import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
  // Icons
  import fileTexMultiple from '@/shared/assets/icons/FileTextMultiple.svg';
  import tableIcon from '@/shared/assets/icons/TableIcon.svg';
  // Components
  import {
    TabsMenu,
    SearchBar,
    SearchToolbar,
    SelectInput,
    NoDocuments,
    PaginateOrScroll,
  } from '@/shared/ui';
  // Composables
  import { useFilterStore } from '@/entities/Filter';
  import { usePermission } from '@/entities/Permission';
  import { useTableFilterModal } from '@/shared/lib/use';
  import { HomologationTable, HomologationFilterModal } from '../ui';
  import { useDocumentRevisionSearch, type DocumentRevision } from '@/entities/Document';
  import { useDocumentSelection } from '@/features/Document';
  import {
    useDocumentSearchTypeStore,
    DocumentType,
    type DocumentRegistrationTabOptionId,
    documentTypeToTabOptionId,
    isDocumentRegistrationTabOptionId,
    tabOptionIdToDocumentType,
    type SearchFilter,
  } from '@/shared/lib';
  // Misc
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import {
    HOMOLOGATION_DEFAULT_PAGE_SIZE,
    getHomologationSearchUrl,
    getHomologationTabConfig,
  } from '../config';
  import { homologationRevisionStageGuard } from '../model';

  const buildDefaultHomologationFilters = (
    documentType: DocumentType = DocumentType.DOCUMENT
  ): SearchFilter => ({
    revisionStatus: '',
    requestDate: '',
    elaborationDate: '',
    approvalDate: '',
    revision: '',
    memberId: '',
    type: documentType,
  });

  type TabOption = { id: DocumentRegistrationTabOptionId; text: string; icon: string };

  const DOCUMENTS_TAB: TabOption = {
    id: 'documentos',
    text: 'Documentos',
    icon: fileTexMultiple,
  };
  const RECORDS_TAB: TabOption = { id: 'registros', text: 'Registros', icon: tableIcon };

  const route = useRoute();
  const router = useRouter();
  const { can } = usePermission();

  // Cada aba tem o seu conjunto de permissoes, em uniao: quem gerencia Registros ve a aba Registros
  // mesmo sem nenhuma permissao de documento, e vice-versa (AC32/AC33).
  const canSeeDocumentsTab = computed(
    () =>
      can('send', 'Document').value ||
      can('manageApproval', 'Homologation').value ||
      can('manageVerification', 'Homologation').value
  );
  const canSeeRecordsTab = computed(
    () =>
      can('manage', 'RecordManagement').value ||
      can('manageApproval', 'Homologation').value ||
      can('manageVerification', 'Homologation').value
  );

  const options = computed<TabOption[]>(() => [
    ...(canSeeDocumentsTab.value ? [DOCUMENTS_TAB] : []),
    ...(canSeeRecordsTab.value ? [RECORDS_TAB] : []),
  ]);

  const urlType = route.query.type as string;
  // Uma aba invisivel nao pode ser aberta pela URL: `?type=REGISTRATION` sem permissao cai em
  // Documentos, em vez de abrir uma aba que o usuario nao teria como fechar.
  const selectedDocumentType = ref<DocumentType>(
    urlType === DocumentType.REGISTRATION && canSeeRecordsTab.value
      ? DocumentType.REGISTRATION
      : DocumentType.DOCUMENT
  );
  const { setSearchType } = useDocumentSearchTypeStore();

  const tabSortState: Record<DocumentRegistrationTabOptionId, { sort: string; order: string }> = {
    documentos: { sort: '', order: 'asc' },
    registros: { sort: '', order: 'asc' },
  };

  let isTabSwitching = false;

  // O objeto fica guardado porque a troca de aba move o endereco dele: o composable le `searchUrl`
  // a cada consulta, e nao no momento em que nasce.
  const searchParams = reactive({
    search: '',
    sort: {},
    filter: buildDefaultHomologationFilters(selectedDocumentType.value),
    searchUrl: getHomologationSearchUrl(selectedDocumentType.value),
    page: 1,
    // `useSearch` nao le este campo: quem manda no tamanho e `selectedQuantity`. Fica aqui porque
    // `SearchParams` exige, e apontando para a mesma constante para nao virar um segundo numero.
    amount: HOMOLOGATION_DEFAULT_PAGE_SIZE,
    systemFilters: ['type'],
  });

  const searchComposable = useDocumentRevisionSearch(searchParams);
  const {
    items: revisions,
    metadata,
    selectedQuantity,
    allItems: allRevisions,
    hasMoreItems: hasMoreRevisions,
    loadingMore,
    isInfiniteScrollMode,
    shouldShowTable,
    shouldShowNoFilterResults,
    shouldShowNoResults,
    shouldShowEmptyPage,
    shouldShowQuantitySelect,
    handleSearch,
    handleSortOptions,
    handleFilter: baseHandleFilter,
    onPageChanged,
    handleQuantityChange,
    applyListQueryReset,
    removeRevisions,
    initSearch,
    cleanup,
    filter,
    searchTerm: currentSearchTerm,
  } = searchComposable;

  provide(SortOrderInjectionKey, {
    sort: searchComposable.sort,
    order: searchComposable.order,
  });

  const filterStore = useFilterStore();
  const { resetSelection } = useDocumentSelection();

  // The shared filter mapper names the collaborator `user`; this endpoint takes the member id
  // as `memberId`, while other screens still send `user` as a user id.
  const handleFilter = async (filters: SearchFilter) => {
    const { user, ...nextFilters } = filters;
    delete nextFilters.type;

    await baseHandleFilter({
      ...buildDefaultHomologationFilters(selectedDocumentType.value),
      ...nextFilters,
      memberId: user ?? '',
      type: selectedDocumentType.value,
    });
  };

  const getCurrentFilters = (): SearchFilter => {
    const { memberId, ...currentFilters } = filter.value;
    delete currentFilters.type;
    return { ...currentFilters, user: memberId };
  };

  const { isFilterActive, openFilterModal } = useTableFilterModal(
    HomologationFilterModal,
    filterStore,
    handleFilter,
    getCurrentFilters
  );

  const handleFilterClicked = () => {
    openFilterModal();
  };

  const selectedTabId = computed(() => documentTypeToTabOptionId(selectedDocumentType.value));
  const tabConfig = computed(() => getHomologationTabConfig(selectedDocumentType.value));

  const handleOptionSelect = async (optionId: string) => {
    if (!isDocumentRegistrationTabOptionId(optionId) || isTabSwitching) {
      return;
    }

    if (!options.value.some((option) => option.id === optionId)) {
      return;
    }

    const nextDocumentType = tabOptionIdToDocumentType(optionId);
    if (nextDocumentType === selectedDocumentType.value) {
      return;
    }

    isTabSwitching = true;

    const currentTabId = selectedTabId.value;
    tabSortState[currentTabId] = {
      sort: searchComposable.sort.value,
      order: searchComposable.order.value,
    };

    selectedDocumentType.value = nextDocumentType;
    searchParams.searchUrl = getHomologationSearchUrl(nextDocumentType);

    // AC17: voltar pelo navegador tem de cair na aba em que o usuario estava. A tela ja le `?type`
    // no setup; passa tambem a escreve-lo.
    await router.replace({ query: { ...route.query, type: nextDocumentType } });

    const targetSort = tabSortState[optionId];

    try {
      filterStore.clearFilters();
      await applyListQueryReset({
        search: '',
        sort: { sort: targetSort.sort, order: targetSort.order },
        filter: buildDefaultHomologationFilters(nextDocumentType),
        quantity: HOMOLOGATION_DEFAULT_PAGE_SIZE,
      });
    } catch (error) {
      console.error('Erro ao aplicar filtro:', error);
    } finally {
      isTabSwitching = false;
    }
  };

  const handleDeleteFrontend = async (ids: number[]) => {
    if (!revisions.value) throw new Error('Não há homologações para deletar');
    if (!ids || ids.length === 0) throw new Error('IDs não definidos');

    try {
      await removeRevisions(ids);

      if (revisions.value.length === 0 && metadata.value!.page > 0) {
        await onPageChanged(metadata.value!.page - 1);
      }
    } catch (error) {
      console.error('Erro ao deletar homologação:', error);
    }
  };

  onMounted(async () => {
    try {
      useFilterStore().clearFilters();
      setSearchType('prefix');
      await initSearch();
    } catch (error) {
      console.error('Erro ao realizar busca inicial:', error);
    }
  });

  onUnmounted(() => {
    resetSelection();
    cleanup();
  });

  // The route's beforeEnter skips query-only changes, such as a notice opened from the bell here.
  onBeforeRouteUpdate(homologationRevisionStageGuard);

  // A query-only navigation reuses this instance, so the tab has to follow `?type` after setup.
  watch(
    () => route.query.type,
    (type) => {
      const nextDocumentType =
        type === DocumentType.REGISTRATION ? DocumentType.REGISTRATION : DocumentType.DOCUMENT;
      void handleOptionSelect(documentTypeToTabOptionId(nextDocumentType));
    }
  );

  onBeforeRouteLeave(() => {
    resetSelection();
    useFilterStore().clearFilters();
  });
</script>

<template>
  <section data-testid="homologation-page">
    <div class="m-6">
      <h3 class="mt-4 flex font-semibold text-xl text-gray-07">
        Homologação
      </h3>

      <TabsMenu
        v-if="options.length > 1"
        :options="options"
        :selected-option="selectedTabId"
        class="mt-4"
        @option-selected="handleOptionSelect"
      />

      <SearchToolbar
        v-if="!shouldShowEmptyPage"
        class="mt-4 mb-6"
      >
        <template #search>
          <SearchBar
            :initial-search-term="currentSearchTerm"
            @search="handleSearch"
          />
        </template>
        <template
          v-if="shouldShowTable"
          #quantity
        >
          <SelectInput
            v-model="selectedQuantity"
            :options="tabConfig.quantityOptions"
            class="cursor-pointer"
            @update:model-value="handleQuantityChange"
          />
        </template>
      </SearchToolbar>
      <PaginateOrScroll
        v-if="shouldShowTable"
        :items="revisions"
        :all-items="allRevisions"
        :has-more-items="hasMoreRevisions"
        :loading-more="loadingMore"
        :metadata="metadata"
        :selected-quantity="selectedQuantity"
        :entity-name="tabConfig.entityName"
        @page-changed="onPageChanged"
      >
        <template #table="{ items }">
          <HomologationTable
            :revisions="items as DocumentRevision[]"
            :page="metadata?.page ?? 1"
            :is-filter-active="isFilterActive"
            :order-options="tabConfig.orderOptions"
            :document-type="selectedDocumentType"
            @delete="handleDeleteFrontend"
            @update:sort="handleSortOptions"
            @update:filter="handleFilter"
            @filter-clicked="handleFilterClicked"
          />
        </template>
      </PaginateOrScroll>

      <NoDocuments
        v-if="shouldShowEmptyPage"
        :title="tabConfig.emptyTitle"
      />
    </div>
  </section>
</template>
